import React, { useState } from "react";
import { FlatList, Image, Text, useWindowDimensions, View } from "react-native";
import { AddCircle } from "iconsax-react-nativejs";
import { useTranslation } from "react-i18next";
import { ActivityIndicator } from "react-native-paper";
import PostCard from "../posts/post-card";
import ScSearchInput from "../ui-kit/search-bar";
import { Post } from "@/src/models/post.model";
import CreatePostModal from "../posts/post-creation-modal";
import ConfirmModal from "../action-modals/confirm-modal";
import ReactorModal from "../posts/post-reactor-modal";
import { usePosts } from "@/src/hooks/features/posts/use-posts";
import { usePermissions } from "@/src/hooks/use-permissions";
import ScButton from "../ui-kit/button";

type GroupPostsFeedProps = Readonly<{
  id: string;
  // posts is no longer passed as a prop — the hook owns the feed
}>;

const GroupPostsFeed = ({ id }: GroupPostsFeedProps) => {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const { canCreate } = usePermissions();
  const canCreatePost = canCreate("post");
  const [currentPost, setCurrentPost] = useState<Post>();

  const {
    displayedPosts,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,

    search,
    setSearch,
    handleSearch,
    isSearchLoading,
    hasSearched,
    handleLoadMoreSearchResults,

    modalOpen,
    setModalOpen,
    editPost,
    setEditPost,
    handleSubmit,
    createPost,

    confirmDeleteOpen,
    setConfirmDeleteOpen,
    setDeletePostId,
    handleDeleteConfirm,

    showReactorModal,
    setReactorModalVisibility,
    handleCloseReactorModal,
  } = usePosts(true, id);

  return (
    <View className="flex-1">
      {/* Search + Create — same UI as the feed */}
      <View className="mb-4">
        {isMobile ? (
          <ScSearchInput
            value={search}
            onChangeText={setSearch}
            handleSearch={handleSearch}
            placeholderKey="LABEL_SEARCH_POSTS"
            surface="screen"
            containerClassName="rounded-2xl"
            onCreatePress={canCreatePost ? () => setModalOpen(true) : undefined}
          />
        ) : (
          <View className="flex-row rounded-2xl border-ash-3 justify-between border bg-chalk p-4">
            <ScSearchInput
              containerClassName="rounded-2xl w-[50%] bg-obsidian"
              placeholderKey="LABEL_SEARCH_POSTS"
              value={search}
              handleSearch={handleSearch}
              onChangeText={setSearch}
            />
            {canCreatePost && (
              <ScButton
                text="BTN_CREATE"
                onPress={() => setModalOpen(true)}
                icon={<AddCircle color="white" />}
              />
            )}
          </View>
        )}
      </View>

      {/* Posts */}
      <FlatList
        data={displayedPosts}
        keyExtractor={(item) => item.ItemId}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={
          displayedPosts.length === 0 ? { flex: 1 } : { gap: 12 }
        }
        ListHeaderComponent={
          hasSearched && displayedPosts.length > 0 ? (
            <View className="flex-row items-center gap-1 px-1 flex-wrap">
              <Text className="text-sm text-gray-500">
                {t("LABEL_SEARCH_RESULTS_FOR")}
              </Text>
              <Text className="text-sm font-semibold text-gray-800">
                {search}
              </Text>
            </View>
          ) : null
        }
        renderItem={({ item }) => (
          <PostCard
            post={item}
            onReactorCount={() => {
              setReactorModalVisibility(true);
              setCurrentPost(item);
            }}
            onEdit={(post) => {
              setEditPost(post);
              setModalOpen(true);
            }}
            onDelete={(post) => {
              setDeletePostId(post.ItemId);
              setConfirmDeleteOpen(true);
            }}
          />
        )}
        onEndReachedThreshold={0}
        onEndReached={() => {
          if (hasSearched) {
            handleLoadMoreSearchResults();
          } else if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
          }
        }}
        ListFooterComponent={
          isFetchingNextPage ? (
            <ActivityIndicator size="small" className="py-4" />
          ) : null
        }
        ListEmptyComponent={
          isSearchLoading ? (
            <View className="items-center py-10">
              <ActivityIndicator size="large" />
            </View>
          ) : hasSearched ? (
            <View className="items-center py-10 gap-3">
              <Image
                source={require("@/src/assets/images/empty.png")}
                style={{ width: 200, height: 200 }}
                resizeMode="contain"
              />
              <Text className="text-gray-800 text-sm font-medium">
                {t("LABEL_NO_RESULTS_FOR")}
              </Text>
              <Text className="text-gray-500 text-sm font-semibold">
                {search}
              </Text>
              <Text className="text-gray-400 text-xs text-center px-8">
                {t("LABEL_TRY_DIFFERENT_KEYWORDS")}
              </Text>
            </View>
          ) : (
            <View className="items-center py-6">
              <Text className="text-gray-dark text-sm mb-4">
                {t("LABEL_EMPTY_POST")}
              </Text>
              <Image
                source={require("@/src/assets/images/empty.png")}
                style={{ width: 300, height: 300 }}
                resizeMode="contain"
              />
            </View>
          )
        }
      />

      {/* Create / edit modal */}
      <CreatePostModal
        visible={modalOpen}
        insideGroup={true}
        groupId={id}
        post={editPost ?? undefined}
        onClose={() => {
          setModalOpen(false);
          setEditPost(null);
        }}
        onSubmit={handleSubmit}
        isPosting={createPost.isPending}
      />

      {/* Confirm delete */}
      <ConfirmModal
        visible={confirmDeleteOpen}
        title="QUEST_CONFIRM_DELETE_POST"
        description="LABEL_THIS_ACTION_CANNOT_BE_UNDONE"
        confirmText="DELETE"
        cancelText="CANCEL"
        onCancel={() => setConfirmDeleteOpen(false)}
        onConfirm={handleDeleteConfirm}
      />

      {/* Reactor modal */}
      <ReactorModal
        showModal={showReactorModal}
        onClose={handleCloseReactorModal}
        reactions={currentPost?.reactionCount ?? {}}
        postId={currentPost?.ItemId}
      />
    </View>
  );
};

export default GroupPostsFeed;
